/**
 * Номера страниц без собственного движка вёрстки.
 *
 * Если документ последним сохранил Word, в нём остаются отметки w:lastRenderedPageBreak —
 * по ним номера точные (на момент сохранения). Иначе страницы оцениваются по объёму
 * текста, кеглю, интервалам, таблицам, рисункам и явным разрывам. Для содержания этого
 * достаточно: Word всё равно пересчитает номера при обновлении поля.
 */

import type { Block, DocModel, Paragraph, Table } from '../model/document';
import { paraFormat } from './format';

export interface Pagination {
  /** Номер страницы (как он печатается), на которой начинается абзац; индекс — номер абзаца. */
  pageOf: number[];
  /** Число физических страниц. */
  total: number;
  /** true — по отметкам последней отрисовки в Word, false — оценка. */
  fromWord: boolean;
}

const cache = new WeakMap<DocModel, Pagination>();

const TWIPS_PER_PT = 20;
/** Средняя ширина знака русского текста в долях кегля (Times New Roman, с пробелами). */
const CHAR_WIDTH = 0.48;
/** Высота строки при одинарном интервале в долях кегля. */
const LINE_HEIGHT = 1.15;
const A4 = { height: 16838, top: 1134, bottom: 1134, width: 11906, left: 1701, right: 850 };

/** estimateOnly — только оценка: после исправлений отметки последней вёрстки Word устарели. */
export function paginate(model: DocModel, opts: { estimateOnly?: boolean } = {}): Pagination {
  if (opts.estimateOnly) return estimate(model);
  const cached = cache.get(model);
  if (cached) return cached;
  const result = fromRendered(model) ?? estimate(model);
  cache.set(model, result);
  return result;
}

/** Номера для печати: перезапуск нумерации в разделах (w:pgNumType w:start). */
function printed(model: DocModel, physical: number[]): number[] {
  const out = physical.slice();
  let offset = 0;
  for (const sec of model.sections) {
    const first = physical[sec.firstParagraph];
    if (first === undefined) continue;
    if (sec.pgNumStart !== undefined) offset = sec.pgNumStart - first;
    for (let i = sec.firstParagraph; i <= sec.lastParagraph && i < out.length; i++) out[i] = physical[i]! + offset;
  }
  return out;
}

function fromRendered(model: DocModel): Pagination | undefined {
  const paras = model.paragraphs;
  if (!paras.some((p) => p.renderedBreaks > 0)) return undefined;
  const physical: number[] = new Array(paras.length).fill(1);
  let page = 1;
  for (const p of paras) {
    const atStart = p.renderedPageStart ? 1 : 0;
    page += atStart;
    physical[p.index] = page;
    page += p.renderedBreaks - atStart;
  }
  // Отметки устаревают, если документ правили не в Word: сверяем с числом страниц в свойствах.
  const pages = model.meta.pages;
  if (pages && Math.abs(page - pages) > Math.max(1, pages * 0.15)) return undefined;
  return { pageOf: printed(model, physical), total: page, fromWord: true };
}

interface Frame {
  /** Высота области текста, пт. */
  height: number;
  /** Ширина области текста, пт. */
  width: number;
}

function frameOf(model: DocModel, section: number): Frame {
  const sec = model.sections[section];
  const m = sec?.margins;
  const h = (sec?.pageHeight ?? A4.height) - Math.abs(m?.top ?? A4.top) - Math.abs(m?.bottom ?? A4.bottom);
  const w = (sec?.pageWidth ?? A4.width) - (m?.left ?? A4.left) - (m?.right ?? A4.right) - (m?.gutter ?? 0);
  return { height: Math.max(200, h / TWIPS_PER_PT), width: Math.max(100, w / TWIPS_PER_PT) };
}

/** Высота абзаца в пунктах при ширине строки width. */
function paragraphHeight(model: DocModel, p: Paragraph, width: number): { lines: number; lineHeight: number; extra: number } {
  const size = (paraFormat(model, p).dominantSize ?? model.styles.defaultsR.sz ?? 20) / 2;
  const pr = p.props;
  const natural = size * LINE_HEIGHT;
  let lineHeight = natural;
  if (pr.line !== undefined) {
    if (pr.lineRule === 'exact') lineHeight = pr.line / TWIPS_PER_PT;
    else if (pr.lineRule === 'atLeast') lineHeight = Math.max(natural, pr.line / TWIPS_PER_PT);
    else lineHeight = natural * (pr.line / 240);
  }
  const indents = (Math.max(0, pr.indLeft ?? 0) + Math.max(0, pr.indRight ?? 0)) / TWIPS_PER_PT;
  const perLine = Math.max(8, (width - indents) / (size * CHAR_WIDTH));
  const first = Math.max(0, (pr.indFirst ?? 0) / TWIPS_PER_PT) / (size * CHAR_WIDTH);
  let lines = 0;
  for (const [k, part] of p.text.replace(/\ufffc/g, '').split('\n').entries()) {
    lines += Math.max(1, Math.ceil((part.length + (k === 0 ? first : 0)) / perLine));
  }
  let objects = 0;
  for (const o of p.objects) if (o.inline && o.heightMm) objects += (o.heightMm / 25.4) * 72;
  if (objects && !p.text.replace(/[\ufffc\s]/g, '')) lines = 0;
  const before = pr.beforeAuto ? 14 : (pr.spacingBefore ?? 0) / TWIPS_PER_PT;
  const after = pr.afterAuto ? 14 : (pr.spacingAfter ?? 0) / TWIPS_PER_PT;
  return { lines, lineHeight, extra: before + after + objects };
}

function blockHeight(model: DocModel, b: Block, width: number): number {
  if (b.kind === 'paragraph') {
    const h = paragraphHeight(model, b, width);
    return h.lines * h.lineHeight + h.extra;
  }
  return tableRows(model, b, width).reduce((a, r) => a + r, 0);
}

/** Высоты строк таблицы: самая высокая ячейка строки плюс поля ячеек. */
function tableRows(model: DocModel, t: Table, width: number): number[] {
  return t.rows.map((row) => {
    const cellWidth = Math.max(40, width / Math.max(1, row.cells.length) - 10);
    let max = 0;
    for (const cell of row.cells) max = Math.max(max, cell.blocks.reduce((a, b) => a + blockHeight(model, b, cellWidth), 0));
    return max + 2;
  });
}

function estimate(model: DocModel): Pagination {
  const paras = model.paragraphs;
  const physical: number[] = new Array(paras.length).fill(1);
  let page = 1;
  let y = 0;

  const place = (height: number, frame: Frame, splittable: boolean, lineHeight: number): number => {
    const remaining = frame.height - y;
    if (height <= remaining) {
      y += height;
      return page;
    }
    // Абзац делится между страницами, если на текущей помещаются хотя бы две строки.
    if (splittable && y > 0 && remaining >= lineHeight * 2) {
      const start = page;
      let rest = height - remaining;
      while (rest > 0) {
        page++;
        y = Math.min(rest, frame.height);
        rest -= frame.height;
      }
      return start;
    }
    if (y > 0) page++;
    const start = page;
    let rest = height;
    while (rest > frame.height) {
      page++;
      rest -= frame.height;
    }
    y = rest;
    return start;
  };

  const assign = (b: Block, p: number): void => {
    if (b.kind === 'paragraph') physical[b.index] = p;
    else for (let i = b.firstParagraph; i <= b.lastParagraph; i++) physical[i] = p;
  };

  for (const b of model.blocks) {
    const first = b.kind === 'paragraph' ? b : paras[b.firstParagraph];
    const frame = frameOf(model, first?.section ?? 0);
    const prev = first ? paras[first.index - 1] : undefined;
    // Абзац из одного разрыва страницы: Word оставляет разрыв со знаком абзаца на текущей странице,
    // а с новой начинает следующий абзац (у него разрыв перед собой из breakAfter) — страницу считаем один раз.
    const breakOnly =
      !!first?.startsWithBreak &&
      first.breakAfter &&
      !first.props.pageBreakBefore &&
      !prev?.breakAfter &&
      !(prev?.hasSectPr && prev.section !== first.section);
    if (first?.pageBreakBefore && !breakOnly && y > 0) {
      page++;
      y = 0;
    }
    if (b.kind === 'paragraph') {
      const h = paragraphHeight(model, b, frame.width);
      assign(b, place(h.lines * h.lineHeight + h.extra, frame, h.extra < h.lineHeight * 2, h.lineHeight));
    } else {
      const rows = tableRows(model, b, frame.width);
      const start = place(rows[0] ?? 0, frame, false, 0);
      assign(b, start);
      // Каждая следующая строка таблицы может уйти на новую страницу.
      let r = 0;
      for (let i = b.firstParagraph; i <= b.lastParagraph; i++) {
        const cell = paras[i]?.cell;
        if (cell && cell.table === b.index && cell.row > r) {
          r = cell.row;
          physical[i] = place(rows[r] ?? 0, frame, false, 0);
        } else if (cell && cell.table === b.index && r > 0) {
          physical[i] = physical[i - 1] ?? start;
        }
      }
    }
  }
  return { pageOf: printed(model, physical), total: page, fromWord: false };
}
